import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import TableAlt from "../components/elements/table/TableAlt";
import ListLayout from "../containers/ListLayout";
import { postData } from "../services/api";
import { useDispatch } from "react-redux";
import { courseListData } from "../store/companiesSlice";
import { API_URL } from '../config/host';
import { setTitle } from "../helpers/MetaTag";
import { formatDate } from "../helpers";
import toastr from "toastr";


const CourseCompleted = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const [statusFilterData, setStatusFilterData] = useState([
    { _id: 'not_started', name: 'Not Started', type: 'checkbox', value: 'not_started', isChecked: true },
    { _id: 'in_progress', name: 'In Progress', type: 'checkbox', value: 'in_progress', isChecked: true },
    { _id: 'completed', name: 'Completed', type: 'checkbox', value: 'completed', isChecked: true },
  ]);

  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [dateRange, setDateRange] = useState({})
  const [list, setList] = useState({
    loading: true,
    data: [],
    count: 0,
  });
  const [tableList, setTableList] = useState([])
  const [sortField, setSortField] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState([]);

  const [courseList, setCourseList] = useState({
    loading: true,
    data: [],
    count: 0,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const langs = useSelector((state) => state?.lang?.list.data);
  const defaultLang = (langs || []).find((item) => item.default)?.code || 'et';

  setTitle(`Radisol | Course Participants`);

  // Mark course as completed
  const handleMarkComplete = useCallback(async (userId, courseId) => {
    if (!window.confirm('Are you sure you want to mark this course as completed?')) {
      return;
    }

    const res = await postData('/admin/course/mark-completed', {
      user_id: userId,
      course_id: courseId
    });

    if (res.status === 200) {
      toastr.success(res.message || 'Course marked as completed');
      setRefreshKey(prev => prev + 1); // Trigger refresh
    } else {
      toastr.error(res.message || 'Failed to mark course as completed');
    }
  }, []);

  // Load user course progress data
  useEffect(() => {
    if (user?._id) {
      const loadData = async () => {
        setList((list) => ({ ...list, loading: true }));

        // Get selected status filters
        const checkedStatusFilter = statusFilterData.filter((item) => item.isChecked);
        const statusFilter = checkedStatusFilter.map((item) => item.value);

        const res = await postData("/admin/course/progress", {
          keyword: keyword,
          limit,
          offset,
          courseIds: selectedCourse,
          statusFilter: statusFilter,
          sortQuery:
            sortField && sortType ? { [sortField]: sortType } : { created_at: -1 },
        });

        if (res.status === 200) {
          setList({
            data: res?.data || [],
            loading: false,
            count: res.count || 0,
          });
        } else {
          console.error('Failed to load course progress:', res.message);
          setList((list) => ({ ...list, loading: false }));
        }
      };

      loadData();
    }
  }, [
    user?._id,
    keyword,
    limit,
    offset,
    sortField,
    sortType,
    statusFilterData,
    selectedCourse,
    defaultLang,
    refreshKey
  ]);

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'completed':
        return '<span class="rounded py-1 px-2 bg-green-100 text-green-600 text-xs font-medium">Completed</span>';
      case 'in_progress':
        return '<span class="rounded py-1 px-2 bg-amber-100 text-amber-600 text-xs font-medium">In Progress</span>';
      case 'not_started':
        return '<span class="rounded py-1 px-2 bg-slate-100 text-slate-600 text-xs font-medium">Not Started</span>';
      default:
        return '-';
    }
  };

  // Load course list for filter
  useEffect(() => {
    if (user?._id) {
      setCourseList((list) => ({ ...list, loading: true }));

      const loadData = async () => {
        const res = await postData('crud/list', {
          table_name: 'course',
          dateRange,
          queryParams: {
            deleted_at: null,
            status: true,
          },
          selectParams: {
            title: true,
            sub_title: true,
            description: true,
            image: {
              $cond: [
                '$image', { $concat: [API_URL, '$image'] }, null
              ]
            },
            ordering: true,
            created_at: true,
          },
          sortQuery: sortField && sortType ? { [sortField]: sortType } : { ordering: 1 },
        });

        if (res.status === 200) {
          setCourseList((list) => ({
            ...list,
            data: res.data.list,
            loading: false,
            count: res.data.count,
          }));
          dispatch(courseListData(res.data.list))
        }
      };

      loadData();
    }
  }, [user?._id, dispatch]);

  // Update tableList when list.data changes
  useEffect(() => {
    setTableList(list.data.map((item) => {
      // For in_progress, show last quiz date; for completed, show completion date
      let dateDisplay = "-";
      if (item.completion_status === 'completed' && item.completed_at) {
        dateDisplay = formatDate(item.completed_at, "DD.MM.YYYY");
      } else if (item.completion_status === 'in_progress' && item.last_quiz_date) {
        dateDisplay = `<span class="text-amber-600">${formatDate(item.last_quiz_date, "DD.MM.YYYY")}</span>`;
      }

      return {
        ...item,
        course_title: item?.courseDetails?.title?.[defaultLang] || item?.courseDetails?.title?.["et"] || "",
        completed_at_display: dateDisplay,
        status_display: getStatusDisplay(item.completion_status),
        progress_display: item.total_quizzes > 0
          ? `<span class="text-sm ${item.passed_quizzes === item.total_quizzes ? 'text-green-600 font-medium' : 'text-slate-600'}">${item.passed_quizzes}/${item.total_quizzes}</span>`
          : '<span class="text-slate-400">-</span>',
        type_display: item.is_company_owner
          ? '<span class="rounded py-1 px-2 bg-blue-100 text-blue-600 text-xs font-medium">Company</span>'
          : '<span class="rounded py-1 px-2 bg-slate-100 text-slate-600 text-xs font-medium">User</span>',
        action_button_download: true,
        action_button_complete: item.is_eligible_for_completion,
      };
    }));
  }, [list?.data, defaultLang]);

  const tableHeadData = [
    {
      _id: 1,
      width: 8,
      title: "Type",
      name: "type_display",
      align: "left",
      sortable: false,
      filterable: false,
      html: true,
    },
    {
      _id: 2,
      width: 12,
      title: "Name",
      name: "full_name",
      align: "left",
      sortable: true,
      filterable: false,
    },
    {
      _id: 3,
      width: 12,
      title: "Company",
      name: "company_name",
      align: "left",
      sortable: true,
      filterable: false,
    },
    {
      _id: 4,
      width: 14,
      title: "Email",
      name: "email",
      align: "left",
      sortable: true,
      filterable: false,
    },
    {
      _id: 5,
      width: 12,
      title: "Course",
      name: "course_title",
      align: "left",
      sortable: true,
      filterable: true,
      filterOptions: courseList.data ? courseList.data.map(item => ({
        ...item,
        type: 'checkbox',
        name: item.title?.[defaultLang] || item.title?.["et"] || "",
        isChecked: selectedCourse.includes(item._id)
      })) : [],
      onFilterChange: (value) => {
        setSelectedCourse((preState) => {
          if (preState.includes(value)) {
            return preState.filter((item) => item !== value)
          } else {
            return [...preState, value]
          }
        });
      },
    },
    {
      _id: 6,
      width: 8,
      title: "Progress",
      name: "progress_display",
      align: "center",
      sortable: false,
      filterable: false,
      html: true,
    },
    {
      _id: 7,
      width: 10,
      title: "Status",
      name: "status_display",
      align: "left",
      sortable: true,
      filterable: true,
      filterOptions: statusFilterData,
      onFilterChange: (id) => {
        setStatusFilterData((data) =>
          data.map((item) =>
            item._id !== id
              ? item
              : {
                ...item,
                isChecked: !item.isChecked,
              }
          )
        );
      },
      html: true,
    },
    {
      _id: 8,
      width: 10,
      title: "Date",
      name: "completed_at_display",
      align: "left",
      sortable: true,
      filterable: false,
      html: true,
    },
    {
      _id: 9,
      width: 8,
      title: "Certificate",
      name: "action_button_download",
      align: "center",
      sortable: false,
      filterable: false,
    },
    {
      _id: 10,
      width: 8,
      title: "Action",
      name: "action_button_complete",
      align: "center",
      sortable: false,
      filterable: false,
    }
  ];

  return (
    <>
      <ListLayout
        showAddButton={false}
        title='Course Participants'
        limit={limit}
        setLimit={setLimit}
        keyword={keyword}
        setKeyword={setKeyword}
        noOfPage={Math.ceil(list.count / limit)}
        onPageChange={(val) => setOffset(val.selected * limit)}
        totalCount={list.count}
        setOffset={setOffset}
        isSearchComponent={true}
      >
        <TableAlt
          tableHeadData={tableHeadData}
          tableData={tableList}
          loading={list.loading}
          sortField={sortField}
          sortType={sortType}
          setSortField={setSortField}
          setSortType={setSortType}
          setList={setList}
          isDraggable={false}
          hasAction={false}
          onMarkComplete={handleMarkComplete}
        />
      </ListLayout>
    </>
  );
};

export default CourseCompleted;
